import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { TriangleAlertIcon } from 'lucide-react'
import {
  getBookingParticipants,
  getCourseRequirements,
  getCreditTypes,
  getGuestInvitations,
  inviteBookingGuest,
  reinviteBookingGuest,
  respondToGuestInvitation,
} from '@/lib/credits-api'
import { ensureCourseConsent } from '@/lib/course-consent'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'

const guestStatus: Record<string, string> = {
  pending: 'Ausstehend',
  accepted: 'Angenommen',
  declined: 'Abgelehnt',
  withdrawn: 'Zurückgezogen',
  cancelled: 'Storniert',
}

export function CourseCreditTypeName({ sessionId }: { sessionId: string }) {
  const requirements = useQuery({
    queryKey: ['session-requirements', sessionId],
    queryFn: () => getCourseRequirements({ sessionId }),
  })
  const types = useQuery({ queryKey: ['credit-types'], queryFn: getCreditTypes })
  const name = types.data?.find((type) => type.id === requirements.data?.credit_type_id)?.name
  return name ? <> ({name})</> : null
}

export function CourseParticipationInfo({ sessionId }: { sessionId: string }) {
  const requirements = useQuery({
    queryKey: ['session-requirements', sessionId],
    queryFn: () => getCourseRequirements({ sessionId }),
  })
  if (!requirements.data) return null
  const course = requirements.data
  if (!course.requires_approval || course.is_approved) return null
  return (
    <Alert className='mb-4 border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400'>
      <TriangleAlertIcon className='h-4 w-4' />
      <AlertTitle>Freischaltung erforderlich</AlertTitle>
      <AlertDescription>
        Für {course.course_name} benötigst du eine Freischaltung. Bitte melde dich bei uns.
      </AlertDescription>
    </Alert>
  )
}

export function BookingGuestsPanel(
  { bookingId, active = true }: { bookingId: string; active?: boolean },
) {
  const client = useQueryClient()
  const [email, setEmail] = useState('')
  const participants = useQuery({
    queryKey: ['booking-participants', bookingId],
    queryFn: () => getBookingParticipants(bookingId),
    retry: false,
  })
  const invite = useMutation({
    mutationFn: () =>
      inviteBookingGuest(bookingId, email.trim()),
    onSuccess: () => {
      setEmail('')
      void client.invalidateQueries()
      toast.success('Einladung versendet.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const respond = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'decline' | 'withdraw' }) =>
      respondToGuestInvitation(id, action),
    onSuccess: (_, { action }) => {
      void client.invalidateQueries()
      toast.success(action === 'decline' ? 'Einladung abgelehnt.' : 'Teilnahme zurückgezogen.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const reinvite = useMutation({
    mutationFn: (id: string) => reinviteBookingGuest(id),
    onSuccess: () => {
      void client.invalidateQueries()
      toast.success('Einladung versendet.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const data = participants.data
  if (!data) return null
  const occupied =
    data.guests.filter((guest) => ['pending', 'accepted'].includes(guest.status)).length
  return (
    <section className='my-3 space-y-3 rounded-lg border p-3 text-sm'>
      <h3 className='font-semibold'>Teilnehmer</h3>
      <p>Hauptbucher: {data.host_name}</p>
      {data.max_guests_per_booking > 0 && (
        <p className='text-muted-foreground'>
          Zusätzliche Teilnehmer: {occupied}/{data.max_guests_per_booking} · ohne weitere Credits
        </p>
      )}
      {data.guests.map((guest) => (
        <div
          key={guest.id}
          className='flex flex-wrap items-center justify-between gap-2 border-t pt-2'
        >
          <div>
            <p>{guest.name}</p>
            <p className='text-xs text-muted-foreground'>
              {guestStatus[guest.status] ?? guest.status} ·{' '}
              {guest.is_approved ? 'freigeschaltet' : 'nicht freigeschaltet'}
            </p>
          </div>
          {active && guest.is_self && guest.status === 'pending' && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={respond.isPending}
              onClick={() => respond.mutate({ id: guest.id, action: 'decline' })}
            >
              Ablehnen
            </Button>
          )}
          {active && (data.is_host || guest.is_self) &&
            (guest.status === 'accepted' || (guest.status === 'pending' && !guest.is_self)) && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={respond.isPending}
              onClick={() => respond.mutate({ id: guest.id, action: 'withdraw' })}
            >
              Zurückziehen
            </Button>
          )}
          {active && data.is_host && ['declined', 'withdrawn'].includes(guest.status) &&
            occupied < data.max_guests_per_booking && (
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={reinvite.isPending}
              onClick={() => reinvite.mutate(guest.id)}
            >
              Erneut einladen
            </Button>
          )}
        </div>
      ))}
      {data.is_host && active && data.max_guests_per_booking > 0 &&
        occupied < data.max_guests_per_booking && (
        <div className='space-y-2'>
          <Input
            aria-label='E-Mail-Adresse des Gastes'
            placeholder='E-Mail-Adresse des Gastes'
            type='email'
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button
            type='button'
            disabled={invite.isPending || !email.trim()}
            onClick={() => invite.mutate()}
          >
            Gast einladen
          </Button>
        </div>
      )}
    </section>
  )
}

export function GuestInvitations() {
  const client = useQueryClient()
  const invitations = useQuery({
    queryKey: queryKeys.guestInvitationsWithPast,
    queryFn: () => getGuestInvitations(true),
  })
  const mutation = useMutation({
    mutationFn: async (
      { id, courseId, action }: { id: string; courseId: string; action: string },
    ) => {
      if (action === 'accept') await ensureCourseConsent({ courseId })
      await respondToGuestInvitation(id, action as 'accept' | 'decline' | 'withdraw')
    },
    onSuccess: () => {
      void client.invalidateQueries()
      toast.success('Einladung aktualisiert.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  if (!invitations.data?.length) {
    return (
      <Card>
        <CardContent className='py-8 text-center text-sm text-muted-foreground'>
          Du warst noch bei keinem Kurs als Gast dabei.
        </CardContent>
      </Card>
    )
  }
  const sorted = [
    ...invitations.data.filter((invitation) => !invitation.is_past),
    ...invitations.data.filter((invitation) => invitation.is_past).reverse(),
  ]
  return (
    <div className='space-y-3'>
      {sorted.map((invitation) => (
        <Card key={invitation.id} className='border-border/80'>
          <CardContent className='space-y-2 p-4'>
            <div className='flex flex-wrap items-center gap-2'>
              <Link
                className='font-semibold underline'
                to={`/kurse/${invitation.course_id}/termine/${invitation.session_id}`}
              >
                {invitation.course_name}
              </Link>
              <Badge variant='secondary'>
                {invitation.is_past ? 'Vergangen' : guestStatus[invitation.status]}
              </Badge>
            </div>
            <p className='text-sm text-muted-foreground'>
              {new Date(invitation.starts_at).toLocaleString('de-AT')} · Hauptbucher:{' '}
              {invitation.host_name}
            </p>
            {!invitation.is_past && (
              <div className='flex flex-wrap gap-2'>
                {invitation.status === 'pending' && (
                  <Button
                    disabled={mutation.isPending}
                    onClick={() =>
                      mutation.mutate({
                        id: invitation.id,
                        courseId: invitation.course_id,
                        action: 'accept',
                      })}
                  >
                    Annehmen
                  </Button>
                )}
                <Button
                  variant='outline'
                  disabled={mutation.isPending}
                  onClick={() =>
                    mutation.mutate({
                      id: invitation.id,
                      courseId: invitation.course_id,
                      action: invitation.status === 'pending' ? 'decline' : 'withdraw',
                    })}
                >
                  {invitation.status === 'pending' ? 'Ablehnen' : 'Teilnahme zurückziehen'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
